// frontend/src/components/PetDetailsHeader.js

import React, { forwardRef, useImperativeHandle, useEffect, useState, useRef } from "react";
import {
  FaPhone,
  FaChevronDown,
  FaHistory,
  FaFileInvoice,
  FaRupeeSign,
  FaTimes,
  FaNotesMedical,
  FaStickyNote,
  FaCamera,
  FaCheckCircle,
} from "react-icons/fa";
import { httpService } from "../../services/httpService";
import { useProfile } from "../../context/ProfileContext";
import { toast } from "react-toastify";
import OrderPaymentDrawer from "../../components/Order/OrderPaymentDrawer";
import OrderFormsSection from "../../components/common/OrderFormsSection";
import PetDetailsBadge from "../../components/common/PetDetailsBadge";
import ClientDetailsDrawer from "../Orders/ClientDetailsDrawer";
import PetDetailsDrawer from "../Orders/PetDetailsDrawer";
import BoardingPetDetailsDrawer from "../Orders/BoardingPetDetailsDrawer";
import GroomingPetDetailsDrawer from "../Orders/GroomingPetDetailsDrawer";
import VetPetDetailsDrawer from "../Orders/VetPetDetailsDrawer";
import AdjustmentAmountCard from "../../components/common/AdjustmentAmountCard";
import config from "../../config";

// Import the S3 base URL from environment or use default
const S3_BASE_URL = (
  config.S3_BASE_URL || 
  process.env.REACT_APP_S3_BASE_URL ||
  "https://flps2025.s3.ap-south-1.amazonaws.com"
).replace(/\/+$/, "");

// URL resolution logic for images
const resolvePreviewUrl = (preview) => {
  if (!preview) return "/icons/default-service.svg";
  if (preview.startsWith("blob:")) return preview;
  if (/^https?:\/\//i.test(preview)) return preview;
  return `${S3_BASE_URL}/${preview.replace(/^\/+/, "")}`;
};

const getStatusStripColor = (label) => {
  switch (label.toLowerCase()) {
    case "started":
    case "check-in":
    case "in progress":
      return "bg-orange-500";
    case "completed":
    case "closed":
    case "checked out":
      return "bg-emerald-600";
    case "cancelled":
    case "no show":
      return "bg-rose-600";
    case "confirmed":
    case "booked":
    case "pending":
      return "bg-indigo-600";
    default:
      return "bg-slate-600";
  }
};

const PetDetailsHeader = forwardRef(({
  orderId,
  vendorPetId,
  onClose,
  onPayNow,
  showOnlySelectedPet = false,
  refreshTrigger = 0,
  liveBillingSummary = null,
  onDirectPaymentSuccess,
  activeServiceRefId = null,
}, ref) => {
  const { profileData, fetchProfile } = useProfile();
  const vendorId = profileData?._id;
  const systemSettings = profileData?.systemSettings; 

  const [order, setOrder] = useState(null);
  const [originalOrderData, setOriginalOrderData] = useState(null);
  const [loadingOrder, setLoadingOrder] = useState(false);
  const [clientOverviewData, setClientOverviewData] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [recentVisits, setRecentVisits] = useState([]);
  const [expandedVisit, setExpandedVisit] = useState(null);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [showPaymentDrawer, setShowPaymentDrawer] = useState(false);
  const [showClientDrawer, setShowClientDrawer] = useState(false);
  const [selectedClientData, setSelectedClientData] = useState(null);
  const [checkinData, setCheckinData] = useState(null);
  const [checkinLoading, setCheckinLoading] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);

  const [activePetPanel, setActivePetPanel] = useState({});
  const [petPhotos, setPetPhotos] = useState({});
  const [petProfiles, setPetProfiles] = useState({});
  const [selectedServicePet, setSelectedServicePet] = useState(null);
  const [selectedServiceType, setSelectedServiceType] = useState(null);

  // ---------- Adjustment state ----------
  const [adjustmentAmount, setAdjustmentAmount] = useState(0);
  const [adjustmentReason, setAdjustmentReason] = useState("");
  const [savingAdjustment, setSavingAdjustment] = useState(false);

  const fetchOrder = async () => {
    try {
      setLoadingOrder(true);
      const res = await httpService.get(
        `/vendororder/getvendororder/${orderId}`,
      );
      if (res.code === 200) {
        setOriginalOrderData(res.data);
        const normalized = normalizeOrderData(res.data);
        setOrder(normalized);
        try {
          const preferredPetId =
            vendorPetId ||
            (normalized.pets && normalized.pets[0] && normalized.pets[0].id);
          if (preferredPetId) {
            const targetPet = (normalized.pets || []).find(
              (p) => String(p.id) === String(preferredPetId),
            );
            if (
              targetPet &&
              Array.isArray(targetPet.services) &&
              targetPet.services.length > 0
            ) {
              setActivePetPanel((prev) => ({
                ...prev,
                [preferredPetId]: "forms",
              }));
            }
          }
        } catch (e) {}
        fetchRecentVisits(res.data.vendor_client_id);
        fetchPetProfiles(normalized.pets.map((p) => p.id));
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingOrder(false);
    }
  };

  useImperativeHandle(ref, () => ({
    refresh: fetchOrder,
  }));

  useEffect(() => {
    if (orderId) fetchOrder();
  }, [orderId]);

  useEffect(() => {
    if (refreshTrigger > 0) {
      fetchOrder();
    }
  }, [refreshTrigger]);

  // Sync adjustment inputs whenever order reloads
  useEffect(() => {
    if (order?.payment) {
      setAdjustmentAmount(Number(order.payment.adjustmentAmount) || 0);
      setAdjustmentReason(order.payment.adjustmentReason || "");
    }
  }, [order?.payment?.adjustmentAmount, order?.payment?.adjustmentReason]);

  const handleSaveAdjustment = async () => {
    if (!orderId || savingAdjustment) return;

    const numericAmount = parseFloat(adjustmentAmount) || 0;
    const originalAmount = parseFloat(order?.payment?.grandTotal || 0);
    const currentAdjustment = parseFloat(order?.payment?.adjustmentAmount || 0);
    const currentReason = order?.payment?.adjustmentReason || "";

    if (numericAmount < 0) {
      toast.error("Adjustment cannot be negative");
      return;
    }
    if (numericAmount > originalAmount) {
      toast.error(
        `Adjustment cannot exceed order amount (₹${originalAmount.toFixed(2)})`
      );
      return;
    }
    if (numericAmount === currentAdjustment && adjustmentReason === currentReason) {
      return;
    }

    try {
      setSavingAdjustment(true);
      const res = await httpService.post("/orderpayment/saveOrderAdjustment", {
        order_id: orderId,
        adjustment_amount: numericAmount,
        adjustment_reason: adjustmentReason,
      });

      if (res?.status) {
        toast.success("Adjustment saved successfully!", { position: "top-center" });
        await fetchOrder();
      } else {
        throw new Error(res?.message || "Failed to save adjustment");
      }
    } catch (err) {
      console.error("Save adjustment error:", err);
      toast.error(
        err?.response?.data?.message || "Failed to save adjustment",
        { position: "top-center" }
      );
      setAdjustmentAmount(currentAdjustment);
      setAdjustmentReason(currentReason);
    } finally {
      setSavingAdjustment(false);
    }
  };

  // Fetch check-in data when order loads
    useEffect(() => {
      if (!orderId || !order?.pets) return;

      const selectedPet = vendorPetId
        ? order.pets.find((pet) => String(pet.id) === String(vendorPetId))
        : order.pets[0];

      if (!selectedPet?.services?.length) return;

      const service = activeServiceRefId
        ? selectedPet.services.find(
            (item) => String(item?.ref_id) === String(activeServiceRefId),
          )
        : selectedPet.services.find((item) => item?.ref_id);

      if (!service?.ref_id) return;

      const serviceType = String(
        service.item_type || service.service_category || "",
      )
        .trim()
        .toLowerCase();

      if (!serviceType) return;

      fetchCheckinData(serviceType, service.ref_id);
    }, [orderId, order, vendorPetId, activeServiceRefId]);

  const normalizeOrderData = (response) => {
    const data = response.data || response;
    const petsMap = {};

    data.order_items.forEach((item) => {
      if (!petsMap[item.vendor_pet_id]) {
        petsMap[item.vendor_pet_id] = {
          id: item.vendor_pet_id,
          client_id: data.vendor_client_id,
          name: item.pet_name,
            image:
              item.image ||
              item.pet_image ||
              item.pet_images?.[0] ||
              item.pet_details?.image ||
              item.pet_details?.pet_images?.[0] ||
              null,
          breed: item.pet_breed,
          type: item.pet_type,
          size: item.pet_size,
          weight: item.weight_kg,
          medical_condition: item.medical_condition || [],
          vaccinations: item.vaccinations || [],
          tags: item.pet_tags || [],
          notes: item.notes || "",
          services: [],
        };
      }

      petsMap[item.vendor_pet_id].services.push({
        id: item._id,
        service_name: item.service_name,
        service_category: item.item_type,
        service_id: item.service_id,
        item_type: item.item_type || item.service_category || "",
        vendor_pet_id: item.vendor_pet_id || item.vendorPetId || "",
        pet_id: item.pet_id || item.petId || "",
        ref_id: item.ref_id || item._id || item.item_id || "",
        form_ids: Array.isArray(item?.form_ids)
          ? item.form_ids
          : item?.form_ids
            ? [item.form_ids]
            : item?.form_id
              ? [item.form_id]
              : [],
        employee_name: item.employee_name,
        technician_id: item.technician_id,
        status: item.status,
        start_date: item.start_date,
        start_time: item.start_time,
        duration: item.duration,
        check_in_date: item.check_in_date || item.start_date,
        check_out_date: item.check_out_date || item.end_date,
        end_date: item.end_date,
        room_type: item.room_type || item.room_name,
        room_number: item.room_number,
        price: item.price,
        final_total: item.final_total,
        payment_status: item.payment_status,
        addons: item.addons || [],
        products: item.products || [],
        otherCharges: item.otherCharges || [],
      });
    });
    
    const grandTotal = Number(data.grand_total) || 0;
    const adjustmentAmount = Number(data.adjustment_amount) || 0;
    const paidAmount = Number(data.total_paid_amount) || 0;

    let finalTotal = Number(data.amount_after_adjustment);
    if (!finalTotal) {
      finalTotal = adjustmentAmount > 0 ? grandTotal - adjustmentAmount : grandTotal;
    }
    finalTotal = Math.max(finalTotal, 0);

    return {
      orderNumber: data.order_number,
      invoiceNumber: data.order_number
        ? `INV-${data.order_number.replace("ORD-", "")}`
        : "",
      status: data.status,
      client: {
        name: [
          data.client_first_name,
          data.client_last_name,
          ]
          .filter(
            (value) =>
              value !== null &&
              value !== undefined &&
              String(value).trim() !== "" &&
              String(value).toLowerCase() !== "null",
            )
          .join(" "),
        label: "",
        phone: data.client_phone,
        email: data.client_email,
        address: data.client_address,
        overview: "",
        recentVisits: [],
        notes: data.notes || "",
        client_id: data.vendor_client_id,
      },
      payment: {
        advanceReceived: paidAmount,
        grandTotal,                                     // ✅ pre-adjustment total
        pendingAmount: Math.max(finalTotal - paidAmount, 0),
        total: finalTotal,
        adjustmentAmount,
        adjustmentReason: data.adjustment_reason || "", // ✅ persisted reason
        amountAfterAdjustment: finalTotal,
      },
      pets: Object.values(petsMap),
    };
  };

  const fetchRecentVisits = async (clientId) => {
    if (!clientId) return;
    try {
      const res = await httpService.get(
        `/vendorbooking/bookingHistory/${clientId}`,
      );
      if (res.status && Array.isArray(res.data)) {
        setRecentVisits(res.data);
      } else {
        setRecentVisits([]);
      }
    } catch (err) {
      console.error(err);
      setRecentVisits([]);
    }
  };

  const handleClientNameClick = async () => {
    try {
      if (!order?.client?.client_id) return;

      const response = await httpService.get(
        `/vendorclient/getvendorclient/${order.client.client_id}`,
      );

      if (response.code === 200) {
        setSelectedClientData(response.data);
      }

      window.petAppointments = recentVisits;
      window.petBoarding = recentVisits;

      setShowClientDrawer(true);
    } catch (err) {
      console.error(err);
      toast.error("Unable to load client details");
    }
  };

  const fetchPetProfiles = async (petIds = []) => {
    const uniqueIds = [...new Set(petIds.filter(Boolean))];
    if (uniqueIds.length === 0) return;

    try {
      const results = await Promise.all(
        uniqueIds.map((id) =>
          httpService.get(`/vendorpet/getvendorpet/${id}`).catch((err) => {
            console.error(`Failed to fetch pet profile for ${id}`, err);
            return null;
          }),
        ),
      );

      const profileMap = {};
      results.forEach((res, i) => {
        if (!res) return;
        const data = res?.data?.data || res?.data || {};
        profileMap[uniqueIds[i]] = {
          age: data.age || "",
          gender: data.gender || "",
        };
      });
      setPetProfiles((prev) => ({ ...prev, ...profileMap }));
    } catch (err) {
      console.error("Failed to fetch pet profiles", err);
    }
  };

  // Fetch Check-in Notes & Photos
    const fetchCheckinData = async (serviceType, serviceRefId) => {
      if (!serviceType || !serviceRefId) {
        console.warn(
          "Missing service type or service_ref_id for fetching check-in data",
          {
            serviceType,
            serviceRefId,
          },
        );
        setCheckinData(null);
        return;
      }

      try {
        setCheckinLoading(true);
        const response = await httpService.get(
          `/checkin/${serviceType}/${serviceRefId}`,
        );

        if (response?.code === 200 || response?.status === 200) {
          const checkinData =
            response?.data?.data?.checkin_data ||
            response?.data?.checkin_data ||
            {};

          const photos = (checkinData?.photos || [])
            .filter((photo) => typeof photo === "string" && photo.trim() !== "")
            .map((photo) => resolvePreviewUrl(photo));

          setCheckinData({
            ...checkinData,
            notes: checkinData?.notes || "",
            photos,
          });
        } else {
          setCheckinData(null);
        }
      } catch (error) {
        console.error(`Error fetching ${serviceType} check-in data:`, error);
        setCheckinData(null);
      } finally {
        setCheckinLoading(false);
      }
    };

  const visitCards = recentVisits.flatMap((order) =>
    (order.order_items || []).map((item) => ({
      orderId: order._id,
      orderNumber: order.order_number,
      createdAt: order.createdAt,
      paymentStatus: order.payment_status,
      ...item,
    })),
  );

  const getPetVisitCards = (petId) => {
    return recentVisits.flatMap((order) =>
      (order.order_items || [])
        .filter((item) => item.vendor_pet_id === petId)
        .map((item) => ({
          orderId: order._id,
          orderNumber: order.order_number,
          createdAt: order.createdAt,
          paymentStatus: order.payment_status,
          ...item,
        })),
    );
  };

    
  const mapPrinterToUrlValue = (printer) => {
    if (!printer || typeof printer !== "string") return null;
    const p = printer.toLowerCase().trim();

    if (p === "thermal") return "thermal";
    if (["dotmatrix", "dot_matrix", "dot-matrix", "dot matrix"].includes(p))
      return "dotmatrix";
    if (["laser", "inkjet", "ink", "ink jet", "ink-jet"].includes(p))
      return "laser";
 if (["letterhead", "letter_head", "letter-head", "letter head"].includes(p))
   return "letterhead"; 
    return null;
  };

    const handleViewInvoice = async () => {
      try {
        const isPosOrder =
          !order?.pets?.length || order.pets.every((p) => !p.services?.length);

        let printerItems = systemSettings?.printer_by_item?.items || [];

        try {
          const fresh = await httpService.get(
            `/vendors/getOnlineProfile?vendorId=${vendorId}`,
          );
          printerItems =
            fresh?.data?.systemSettings?.printer_by_item?.items || printerItems;
        } catch (e) {
          console.error("Failed to refresh printer settings:", e);
        }

        const targetName = isPosOrder ? "Invoice – POS" : "Invoice – Service";

        const invoiceConfig = printerItems.find(
          (item) =>
            item.name === targetName ||
            (item.name || "").toLowerCase() === targetName.toLowerCase(),
        );

        const selectedPrinter = invoiceConfig?.printer;
        const printerValue = mapPrinterToUrlValue(selectedPrinter);

         if (printerValue) {
           const baseUrl =
             process.env.REACT_APP_ADMIN_URL || "http://localhost:3000";
           const previewUrl = `${baseUrl}/admin/invoicePreview/${printerValue}/${orderId}`;
           window.open(previewUrl, "_blank", "noopener,noreferrer");
           return;
         }

        if (!orderId) return;

        setInvoiceLoading(true);
        const response = await httpService.get(
          `/invoice/getorderinvoice?orderId=${orderId}&vendorId=${vendorId}`,
        );
        if (response.status && response.data?.invoice_url) {
          window.open(response.data.invoice_url, "_blank");
        } else {
          toast.error("Invoice not found");
        }
      } catch (err) {
        console.error("Failed fetching invoice", err);
        toast.error("Unable to fetch invoice");
      } finally {
        setInvoiceLoading(false);
      }
    };

  const fetchClientOverview = async () => {
    if (!order?.client?.client_id) return;
    try {
      setLoadingStats(true);
      const response = await httpService.get(
        `/vendorclient/getVendorClientPetsOverviewById/${order.client.client_id}`,
      );
      if (response.code === 200) {
        setClientOverviewData(response.data?.data || response.data);
      }
    } catch (error) {
      console.error("Error fetching client overview:", error);
    } finally {
      setLoadingStats(false);
    }
  };

  useEffect(() => {
    if (order?.client?.client_id) {
      fetchClientOverview();
    }
  }, [order?.client?.client_id]);
  
  const hasRefreshedProfile = useRef(false);
  useEffect(() => {
    if (hasRefreshedProfile.current) return;
    hasRefreshedProfile.current = true;
    if (fetchProfile) {
      fetchProfile().catch((err) => console.error("Failed to refresh profile", err));
    }
  }, []);
  const togglePetPanel = (petId, panel) => {
    setActivePetPanel((prev) => ({
      ...prev,
      [petId]: prev[petId] === panel ? null : panel,
    }));
  };

  const handlePhotoUpload = (petId, fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const urls = files.map((file) => URL.createObjectURL(file));
    setPetPhotos((prev) => ({
      ...prev,
      [petId]: [...(prev[petId] || []), ...urls],
    }));
  };

  if (loadingOrder) {
    return (
      <div className="shrink-0 border-b border-slate-200 bg-slate-50 px-5 py-5 animate-pulse">
        <div className="flex gap-4">
          <div className="w-16 h-16 rounded-xl bg-slate-200" />
          <div className="flex-1 space-y-2">
            <div className="h-5 bg-slate-200 rounded w-1/3" />
            <div className="h-3 bg-slate-200 rounded w-1/2" />
            <div className="h-3 bg-slate-200 rounded w-1/4" />
          </div>
        </div>
      </div>
    );
  }

  if (!order) {
    return null;
  }

  const clientName = order?.client?.name || "";
  const clientPhone = order?.client?.phone || "";
  const clientDisplayName = clientName || clientPhone || "—";
  const orderNumber = order?.orderNumber || "—";
  const invoiceNumber = order?.invoiceNumber || "";
  const payment = liveBillingSummary
    ? {
        advanceReceived: liveBillingSummary.totalPaid,
        pendingAmount: liveBillingSummary.balanceDue,
        total: liveBillingSummary.totalCharges,
      }
    : order?.payment || {
        advanceReceived: 0,
        pendingAmount: 0,
        total: 0,
      };
  const allPets = order?.pets || [];

  // Prefer the selected pet's service status; fall back to order status.
  const statusBadge = (() => {
    const selectedPet = vendorPetId
      ? allPets.find((p) => String(p.id) === String(vendorPetId))
      : allPets[0];

    const matchedService = activeServiceRefId
      ? selectedPet?.services?.find(
          (s) => String(s.ref_id) === String(activeServiceRefId),
        )
      : null;

    const rawStatus = String(
      (matchedService || selectedPet?.services?.[0])?.status ||
        order?.status ||
        "",
    )
      .trim()
      .toLowerCase();

    if (!rawStatus) return null;

    const labelMap = {
      started: "Started",
      checkin: "Started",
      check_in: "Started",
      in_progress: "In Progress",
      completed: "Completed",
      complete: "Completed",
      closed: "Closed",
      checkout: "Checked Out",
      checked_out: "Checked Out",
      cancelled: "Cancelled",
      cancel: "Cancelled",
      "no show": "No Show",
      confirmed: "Confirmed",
      booked: "Booked",
      pending: "Pending",
    };

    const label =
      labelMap[rawStatus] ||
      rawStatus.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

    const tone =
      ["completed", "complete", "closed", "checkout", "checked_out"].includes(
        rawStatus,
      )
        ? "bg-emerald-50 text-emerald-700"
        : ["cancelled", "cancel", "no show"].includes(rawStatus)
          ? "bg-rose-50 text-rose-700"
          : ["started", "checkin", "check_in", "in_progress"].includes(rawStatus)
            ? "bg-green-50 text-green-700"
            : "bg-slate-100 text-slate-700";

    return { label, tone };
  })();

  const openPetServiceDrawer = (pet, profile, service) => {
    const serviceType = String(
      service?.item_type || service?.service_category || "",
    ).toLowerCase();
    const petData = {
      _id: pet.id,
      vendor_client_id: pet.client_id || order?.client?.client_id,
      name: pet.name,
      breed: { breed_name: pet.breed },
      pet_type: { name: pet.type },
      size_details: { size_label: pet.size },
      weight_kg: pet.weight,
      age: profile?.age,
      gender: profile?.gender,
      tags: pet.tags || [],
      medical_condition: pet.medical_condition || [],
      vaccinations: pet.vaccinations || [],
    };

    setSelectedServicePet(petData);
    setSelectedServiceType(serviceType);
  };

  const closePetServiceDrawer = () => {
    setSelectedServicePet(null);
    setSelectedServiceType(null);
  };

  const pets =
    showOnlySelectedPet && vendorPetId
      ? allPets.filter((pet) => String(pet.id) === String(vendorPetId))
      : allPets;

  const petTabButtonClass = (isActive) =>
    `inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-full transition ${
      isActive
        ? "bg-indigo-100 text-indigo-700"
        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
    }`;

  return (
    <>
      {/* Dynamic status strip */}
      {statusBadge && (
        <div
          className={`w-full text-white text-center py-2 font-bold text-sm uppercase tracking-wider flex items-center justify-center gap-2 ${getStatusStripColor(statusBadge.label)}`}
        >
          <FaCheckCircle size={14} />
          {statusBadge.label}
        </div>
      )}

      <div className="flex flex-col lg:flex-row items-stretch border-b border-gray-200 bg-white flex-shrink-0">
        <div className="w-full lg:w-1/2 min-w-0 px-5 py-4 border-b lg:border-b-0 lg:border-r border-gray-200 space-y-5">
          {pets.length === 0 && (
            <p className="text-sm text-gray-500">No pet details found.</p>
          )}

          {pets.map((pet, index) => {
            const petId = pet.id || index;
            const profile = petProfiles[petId] || {};
            const activePanel = activePetPanel[petId] || null;
            const petVisits = getPetVisitCards(petId);

            return (
              <div key={petId} className="space-y-2">
                <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <PetDetailsBadge
                      petId={petId}
                      petName={pet.name}
                      petType={pet.type}
                      petBreed={pet.breed}
                      petSize={pet.size}
                      petWeight={pet.weight}
                      petImage={pet.image}
                      petAge={profile.age}
                      petGender={profile.gender}
                      petTags={pet.tags}
                      petMedicalConditions={pet.medical_condition}
                      petVaccinations={pet.vaccinations}
                      petVendorClientId={pet.client_id}
                      petVendorId={vendorId}
                      triggerGetClientApi={true}
                      showIcon={true}
                      showBreed={true}
                      showSize={true}
                      showWeight={true}
                      showAge={!!profile.age}
                      showGender={!!profile.gender}
                      showTags={true}
                      iconSize="w-6 h-6"
                      textSize="text-lg"
                      fontWeight="font-bold"
                      color="text-slate-900"
                      underlineColor="border-slate-900"
                      badgeClassName="inline-flex items-center gap-2 transition-colors"
                      enableDrawer={false}
                      onClick={() =>
                        openPetServiceDrawer(pet, profile, pet.services?.[0])
                      }
                      onRefresh={() => {
                        fetchOrder();
                      }}
                      showEditButton={true}
                      existingPets={pets}
                      onEditSave={() => {
                        fetchOrder();
                      }}
                    />
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 pt-1">
                  <button
                    className={petTabButtonClass(
                      activePanel === "recentVisits",
                    )}
                    onClick={() => togglePetPanel(petId, "recentVisits")}
                  >
                    <FaHistory className="text-xs" /> Recent Visits
                    <FaChevronDown
                      className={`text-[10px] transition-transform ${
                        activePanel === "recentVisits" ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                  <button
                    className={petTabButtonClass(
                      activePanel === "medicalCondition",
                    )}
                    onClick={() => togglePetPanel(petId, "medicalCondition")}
                  >
                    <FaNotesMedical className="text-xs" /> Medical Condition
                    <FaChevronDown
                      className={`text-[10px] transition-transform ${
                        activePanel === "medicalCondition" ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                  <button
                    className={petTabButtonClass(activePanel === "notes")}
                    onClick={() => togglePetPanel(petId, "notes")}
                  >
                    <FaStickyNote className="text-xs" /> Notes
                    <FaChevronDown
                      className={`text-[10px] transition-transform ${
                        activePanel === "notes" ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                  <button
                    className={petTabButtonClass(activePanel === "photos")}
                    onClick={() => togglePetPanel(petId, "photos")}
                  >
                    <FaCamera className="text-xs" /> Photos
                    <FaChevronDown
                      className={`text-[10px] transition-transform ${
                        activePanel === "photos" ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                  {pet.services.length > 0 && (
                    <button
                      className={petTabButtonClass(activePanel === "forms")}
                      onClick={() => togglePetPanel(petId, "forms")}
                    >
                      <FaFileInvoice className="text-xs" /> Forms
                      <FaChevronDown
                        className={`text-[10px] transition-transform ${
                          activePanel === "forms" ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                  )}
                </div>

                {activePanel && (
                  <div className="bg-gray-50 rounded-lg p-3 max-h-48 overflow-y-auto text-sm">
                    {activePanel === "recentVisits" && (
                      <div className="space-y-2">
                        {petVisits.length === 0 ? (
                          <p className="text-sm text-gray-500 py-1">
                            No recent visits found for this pet.
                          </p>
                        ) : (
                          petVisits.map((visit, vIndex) => (
                            <div
                              key={vIndex}
                              className="rounded-lg overflow-hidden border border-gray-200 bg-white"
                            >
                              <button
                                onClick={() =>
                                  setExpandedVisit(
                                    expandedVisit === `${petId}-${vIndex}`
                                      ? null
                                      : `${petId}-${vIndex}`,
                                  )
                                }
                                className="w-full px-2 py-1.5 flex items-center justify-between"
                              >
                                <div className="flex flex-wrap items-center gap-2 text-sm">
                                  <span className="bg-gray-100 px-2 py-0.5 rounded-md font-medium">
                                    #{visit.orderNumber}
                                  </span>
                                  <span>
                                    {new Date(
                                      visit.createdAt,
                                    ).toLocaleDateString("en-GB", {
                                      day: "2-digit",
                                      month: "short",
                                      year: "numeric",
                                    })}
                                  </span>
                                  <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded-md font-medium">
                                    ₹
                                    {Math.round(
                                      visit.total_price ||
                                        visit.final_total ||
                                        0,
                                    )}
                                  </span>
                                  <span className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded-md">
                                    {visit.status
                                      ?.toLowerCase()
                                      .replace(/\b\w/g, (char) =>
                                        char.toUpperCase(),
                                      )}
                                  </span>
                                </div>
                                <FaChevronDown
                                  className={`text-xs transition ${
                                    expandedVisit === `${petId}-${vIndex}`
                                      ? "rotate-180"
                                      : ""
                                  }`}
                                />
                              </button>
                              {expandedVisit === `${petId}-${vIndex}` && (
                                <div className="border-t border-gray-200 p-2 space-y-1 text-sm">
                                  <div>
                                    <strong>Service</strong> :{" "}
                                    {visit.service_name}
                                  </div>
                                  <div>
                                    <strong>Category</strong> :{" "}
                                    {visit.item_type === "vet_clinic"
                                      ? "Vet"
                                      : visit.item_type}
                                  </div>
                                  <div>
                                    <strong>Staff</strong> :{" "}
                                    {visit.employee_name || "-"}
                                  </div>
                                  <div>
                                    <strong>Date</strong> :{" "}
                                    {visit.start_date
                                      ? new Date(
                                          visit.start_date,
                                        ).toLocaleDateString("en-GB")
                                      : "-"}
                                  </div>
                                  <div>
                                    <strong>Time</strong> :{" "}
                                    {visit.start_time || "-"}
                                  </div>
                                  <div>
                                    <strong>Pet</strong> : {visit.pet_name}
                                  </div>
                                  <div>
                                    <strong>Breed</strong> : {visit.pet_breed}
                                  </div>
                                </div>
                              )}
                            </div>
                          ))
                        )}
                      </div>
                    )}

                    {activePanel === "medicalCondition" && (
                      <div className="flex flex-wrap gap-2">
                        {pet.medical_condition &&
                        pet.medical_condition.length > 0 ? (
                          pet.medical_condition.map((cond, cIndex) => (
                            <span
                              key={cIndex}
                              className="bg-red-50 border border-red-200 text-red-700 px-2.5 py-1 rounded-full text-sm"
                            >
                              {typeof cond === "string"
                                ? cond
                                : cond.name || cond.condition}
                            </span>
                          ))
                        ) : (
                          <p className="text-sm text-gray-500">
                            No medical conditions recorded.
                          </p>
                        )}
                      </div>
                    )}

                    {activePanel === "notes" && (
                      <div className="mt-2">
                        {checkinLoading ? (
                          <div className="flex items-center justify-center py-4">
                            <div className="animate-pulse flex items-center gap-2 text-sm text-slate-500">
                              <div className="w-4 h-4 bg-slate-200 rounded-full"></div>
                              Loading notes...
                            </div>
                          </div>
                        ) : checkinData?.notes ? (
                          <div className="bg-white rounded-lg border border-gray-200 p-3">
                            <div className="flex items-start gap-2">
                              <FaStickyNote className="text-indigo-500 mt-0.5 flex-shrink-0" />
                              <p className="text-sm text-gray-700 whitespace-pre-wrap">
                                {checkinData.notes}
                              </p>
                            </div>
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500 py-2">
                            No check-in notes available.
                          </p>
                        )}
                      </div>
                    )}

                    {activePanel === "photos" && (
                      <div className="space-y-2">
                        {checkinLoading ? (
                          <div className="flex items-center justify-center py-4">
                            <div className="animate-pulse flex items-center gap-2 text-sm text-slate-500">
                              <div className="w-4 h-4 bg-slate-200 rounded-full"></div>
                              Loading photos...
                            </div>
                          </div>
                        ) : checkinData?.photos &&
                          checkinData.photos.length > 0 ? (
                          <>
                            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                              {checkinData.photos.map((photo, index) => {
                            
                                const imageUrl =
                                  photo &&
                                  typeof photo === "string" &&
                                  photo.startsWith("http")
                                    ? photo
                                    : resolvePreviewUrl(photo);

                                return (
                                  <div
                                    key={index}
                                    className="relative aspect-square rounded-lg overflow-hidden border border-gray-200 cursor-pointer hover:ring-2 hover:ring-indigo-500 transition-all group"
                                    onClick={() => setPreviewImage(imageUrl)}
                                  >
                                    <img
                                      src={imageUrl}
                                      alt={`Check-in photo ${index + 1}`}
                                      className="w-full h-full object-cover"
                                      onError={(e) => {
                                        e.currentTarget.onerror = null;
                                        e.currentTarget.src =
                                          "/icons/default-service.svg";
                                      }}
                                    />
                                    <div className="absolute bottom-1 right-1 bg-black/50 text-white text-[10px] px-1.5 py-0.5 rounded">
                                      {index + 1}/{checkinData.photos.length}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                              {checkinData.photos.length} photo
                              {checkinData.photos.length > 1 ? "s" : ""}{" "}
                              uploaded during check-in
                            </p>
                          </>
                        ) : (
                          <div className="text-center py-4">
                            <FaCamera className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                            <p className="text-sm text-gray-500">
                              No check-in photos available.
                            </p>
                          </div>
                        )}
                      </div>
                    )}

                    {activePanel === "forms" && (
                      <div className="w-full rounded-2xl border border-gray-200 bg-white p-2.5 shadow-sm">
                        <OrderFormsSection
                          orderId={orderId}
                          item={
                            pet.services.find(
                              (service) =>
                                Array.isArray(service.form_ids) &&
                                service.form_ids.length > 0,
                            ) ||
                            pet.services[0] ||
                            {}
                          }
                          clientPhone={order?.client?.phone || ""}
                          hideBypassBlock={true}
                          hideTitle={true}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="w-full lg:w-1/2 min-w-0 px-5 py-4 relative flex flex-col">
          {onClose && (
            <button
              onClick={onClose}
              className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition text-gray-500"
            >
              <FaTimes className="text-sm" />
            </button>
          )}

          <div className="flex flex-wrap items-center gap-3 pr-10">
              <span
                onClick={handleClientNameClick}
                className="text-lg font-bold text-blue-600 hover:underline cursor-pointer transition-all"
              >
                {clientDisplayName}
              </span>

              {clientName && clientPhone && (
                <>
                  <span className="text-gray-300">|</span>
                  <span className="flex items-center gap-1 text-sm text-gray-600">
                    <FaPhone className="text-gray-400 text-xs" />
                    {clientPhone}
                  </span>
                </>
              )}

              <span className="text-gray-300">|</span>
              <span className="text-sm font-semibold text-slate-700 whitespace-nowrap">
                #{orderNumber}
              </span>

              <button
                onClick={handleViewInvoice}
                disabled={invoiceLoading}
                className="inline-flex items-center gap-1 text-sm font-medium text-indigo-600 bg-indigo-50 px-3 py-1.5 rounded-full hover:bg-indigo-100 transition disabled:opacity-50 ml-auto sm:ml-2 mt-2 sm:mt-0"
              >
                <FaFileInvoice className="text-xs" />
                {invoiceLoading ? "Loading..." : `Invoice ${invoiceNumber}`}
              </button>
            </div>

          <div className="bg-gray-50 rounded-lg p-3">
            <div className="flex items-center justify-between gap-2">
              <div className="flex-1">
                <span className="text-gray-400 text-xs whitespace-nowrap block">
                  Amount Received
                </span>
                <div className="text-sm font-medium text-green-600 flex items-center">
                  <FaRupeeSign className="w-3 h-3 mr-0.5" />
                  <span>{Number(payment.advanceReceived || 0).toFixed(0)}</span>
                </div>
              </div>

              <div className="w-px h-10 bg-gray-300"></div>

              <div className="flex-1">
                <span className="text-gray-400 text-xs whitespace-nowrap block">
                  Pending Amount
                </span>
                <div className="text-sm font-medium text-red-600 flex items-center">
                  <FaRupeeSign className="w-3 h-3 mr-0.5" />
                  <span>{Number(payment.pendingAmount || 0).toFixed(0)}</span>
                </div>
              </div>

              <div className="w-px h-10 bg-gray-300"></div>

              <div className="flex-1">
                <span className="text-gray-400 text-xs whitespace-nowrap block">
                  Total
                </span>
                <div className="text-sm font-medium text-blue-700 flex items-center">
                  <FaRupeeSign className="w-3 h-3 mr-0.5" />
                  <span>{Number(payment.total || 0).toFixed(0)}</span>
                </div>
              </div>

              <div className="w-px h-10 bg-gray-300"></div>

              <div className="flex-1 flex justify-end">
                <button
                  onClick={onPayNow || (() => setShowPaymentDrawer(true))}
                  disabled={Number(payment.pendingAmount) <= 0}
                  className={`text-white text-sm font-medium px-4 py-1.5 rounded-md transition ${
                    Number(payment.pendingAmount) > 0
                      ? "bg-green-600 hover:bg-green-700"
                      : "bg-gray-400 cursor-not-allowed"
                  }`}
                >
                  {Number(payment.pendingAmount) <= 0 ? "Paid" : "Pay Now"}
                </button>
              </div>
            </div>
          </div>

          {/* ✅ Adjustment card — placed BELOW the payment summary row */}
          {!liveBillingSummary && (
            <AdjustmentAmountCard
              adjustmentAmount={adjustmentAmount}
              adjustmentReason={adjustmentReason}
              onAdjustmentChange={setAdjustmentAmount}
              onAdjustmentReasonChange={setAdjustmentReason}
              orderTotal={parseFloat(order?.payment?.grandTotal || 0)}
              maxAmount={parseFloat(order?.payment?.grandTotal || 0)}
              showSaveButton
              onSave={handleSaveAdjustment}
              saving={savingAdjustment}
              savedValue={parseFloat(order?.payment?.adjustmentAmount || 0)}
              savedReason={order?.payment?.adjustmentReason || ""}
              className="mt-2"
            />
          )}
        </div>
      </div>

      {previewImage && (
        <div
          className="fixed inset-0 z-[9999] bg-black/80 flex items-center justify-center p-4"
          onClick={() => setPreviewImage(null)}
        >
          <div
            className="relative w-full max-w-5xl max-h-[90vh] flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              type="button"
              onClick={() => setPreviewImage(null)}
              className="absolute -top-3 -right-3 z-10 w-9 h-9 rounded-full bg-white text-gray-700 shadow-lg flex items-center justify-center hover:bg-gray-100 transition"
            >
              <FaTimes className="text-sm" />
            </button>

            {/* Large image */}
            <img
              src={previewImage}
              alt="Boarding check-in preview"
              className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
              onError={(e) => {
                e.currentTarget.onerror = null;
                setPreviewImage(null);
              }}
            />
          </div>
        </div>
      )}

      <OrderPaymentDrawer
        isOpen={showPaymentDrawer}
        onClose={() => setShowPaymentDrawer(false)}
        orderId={orderId}
        onPaymentSuccess={() => {
          setShowPaymentDrawer(false);
          fetchOrder();
          onDirectPaymentSuccess?.();
        }}
      />
      {showClientDrawer && (
        <ClientDetailsDrawer
          isOpen={showClientDrawer}
          onClose={() => setShowClientDrawer(false)}
          clientData={selectedClientData}
          onRefresh={fetchOrder}
        />
      )}
      {selectedServicePet &&
        !["boarding", "day_care", "grooming", "vet_clinic", "vet"].includes(
          selectedServiceType,
        ) && (
          <PetDetailsDrawer
            isOpen={true}
            onClose={closePetServiceDrawer}
            petData={selectedServicePet}
            clientData={order?.client}
            clientAppointments={recentVisits}
            clientBoarding={recentVisits}
            triggergetclientapi={true}
            onRefresh={fetchOrder}
          />
        )}
      {selectedServicePet &&
        ["boarding", "day_care"].includes(selectedServiceType) && (
          <BoardingPetDetailsDrawer
            isOpen={true}
            onClose={closePetServiceDrawer}
            petData={selectedServicePet}
            clientData={order?.client}
            clientBoarding={recentVisits}
            clientAppointments={recentVisits}
            triggergetclientapi={true}
            onRefresh={fetchOrder}
          />
        )}
      {selectedServicePet && selectedServiceType === "grooming" && (
        <GroomingPetDetailsDrawer
          isOpen={true}
          onClose={closePetServiceDrawer}
          petData={selectedServicePet}
          clientData={order?.client}
          onRefresh={fetchOrder}
        />
      )}
      {selectedServicePet && ["vet_clinic", "vet"].includes(selectedServiceType) && (
        <VetPetDetailsDrawer
          isOpen={true}
          onClose={closePetServiceDrawer}
          petData={selectedServicePet}
          clientData={order?.client}
          onRefresh={fetchOrder}
        />
      )}
    </>
  );
});

PetDetailsHeader.displayName = "PetDetailsHeader";

export default PetDetailsHeader;